import React from "react";
import { HiChevronRight, HiOutlineTrash } from "react-icons/hi";
import { FaLock } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { CustomButton } from "./CustomButton";
import { getFieldTypeInfo, getFieldValuePreview } from "../../utils/fieldUtils";
import NoTranslate from "./NoTranslate";

interface FieldCardProps {
  fieldKey: string;
  fieldValue: unknown;
  onEdit: (fieldKey: string) => void;
  onDelete?: (fieldKey: string) => void;
  isReadOnly?: boolean;
  readOnlyTooltip?: string;
}

export const FieldCard: React.FC<FieldCardProps> = ({
  fieldKey,
  fieldValue,
  onEdit,
  onDelete,
  isReadOnly = false,
  readOnlyTooltip,
}) => {
  const { t } = useTranslation();
  const typeInfo = getFieldTypeInfo(fieldValue);
  const TypeIcon = typeInfo.icon;
  const valuePreview = getFieldValuePreview(fieldValue, t);

  return (
    <div className="group relative flex flex-col rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 hover:bg-gray-50">
      <div className="flex items-center gap-2">
        <div className={`p-2 rounded-lg ${typeInfo.bgColor} shrink-0`}>
          <TypeIcon className={typeInfo.color} size={18} />
        </div>
        <button
          type="button"
          onClick={() => onEdit(fieldKey)}
          title={fieldKey}
          className="flex min-w-0 flex-1 items-center gap-1 text-left after:absolute after:inset-0 after:content-['']"
        >
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 group-hover:text-blue-700">
            <NoTranslate>{fieldKey}</NoTranslate>
          </span>
          <HiChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-300 transition-colors group-hover:text-blue-700" />
        </button>
        {isReadOnly ? (
          <FaLock
            size={16}
            className="relative z-10 shrink-0 text-gray-400"
            title={readOnlyTooltip || (t("common.readOnly") as string)}
          />
        ) : (
          onDelete && (
            <CustomButton
              variant="none"
              icon={<HiOutlineTrash size={18} />}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(fieldKey);
              }}
              title=""
              size="icon"
              tooltip={t("common.delete") as string}
              className="relative z-10 shrink-0 rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
            />
          )
        )}
      </div>

      <p
        className="mt-2 line-clamp-2 text-xs text-gray-500"
        title={valuePreview}
      >
        <NoTranslate>{valuePreview || "-"}</NoTranslate>
      </p>
    </div>
  );
};

export default FieldCard;
